// Any number of clicks in the highlighted zone mark cores Next zone is the only thing that
// advances.

import type { SoilSamplePoint } from "../types/contract";
import { pointInPolygons, polygonsOf } from "./samplingDesign";
import { sampleBlobToM3 } from "./soilLayers";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export const CORE_PROPS = [
  { key: "ph", label: "pH", stack: "model__uncorrected_ph", digits: 1 },
  { key: "p", label: "P", stack: "model__uncorrected_p", digits: 1 },
  { key: "k", label: "K", stack: "model__uncorrected_k", digits: 1 },
  { key: "om", label: "OM", stack: "model__uncorrected_om", digits: 1 },
  { key: "cec", label: "CEC", stack: "model__uncorrected_cec", digits: 1 },
] as const;

export type CorePropKey = (typeof CORE_PROPS)[number]["key"];
export type CoreValues = Record<CorePropKey, number | null>;

export interface GeniusZone {
  index: number;
  number: number;
  /* * Matches lab `zone_ref`: `properties.zone` when the file stamps it, else `zone_number`, else position. */
  ordinal: number;
  label: string;
  feature: GeoJSON.Feature;
}

function numericProp(
  props: Record<string, unknown>,
  key: string,
): number | null {
  const v = props[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function zonesFromFc(
  fc: GeoJSON.FeatureCollection | null | undefined,
): GeniusZone[] {
  const features = (fc?.features ?? []).filter((f) => polygonsOf(f).length);
  const explicit = features.some(
    (f) =>
      numericProp((f.properties ?? {}) as Record<string, unknown>, "zone") !=
      null,
  );
  const zones: GeniusZone[] = [];
  for (const feature of features) {
    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const stamped = numericProp(props, "zone");
    const numbered = numericProp(props, "zone_number");
    const number = numbered ?? stamped ?? zones.length + 1;
    const ordinal = explicit
      ? (stamped ?? numbered ?? zones.length + 1)
      : (stamped ?? numbered ?? zones.length + 1);
    const name =
      typeof props.zone_name === "string" && props.zone_name.trim()
        ? props.zone_name.trim()
        : null;
    zones.push({
      index: zones.length,
      number,
      ordinal,
      label: name ?? `Zone ${number}`,
      feature,
    });
  }
  return zones;
}

export function clickInZone(
  zone: GeniusZone,
  lng: number,
  lat: number,
): boolean {
  return pointInPolygons({ lng, lat }, polygonsOf(zone.feature));
}

/* * The stack cell the click falls in. Nearest center only if it is within one cell (~12 m). */
export function cellUnderClick(
  geoms: { type: string; coordinates: unknown }[],
  centroids: { lng: number; lat: number }[],
  lng: number,
  lat: number,
): number {
  const pt = { lng, lat };
  for (let i = 0; i < geoms.length; i++) {
    const polys = polygonsOf(geoms[i]);
    if (polys.length && pointInPolygons(pt, polys)) return i;
  }
  const n = nearestCellIndex(centroids, lng, lat);
  if (n < 0) return -1;
  const c = centroids[n]!;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat * Math.PI) / 180);
  const dx = (c.lng - lng) * kx;
  const dy = (c.lat - lat) * METRES_PER_DEGREE_LAT;
  return dx * dx + dy * dy <= 20 * 20 ? n : -1;
}
export function nearestCellIndex(
  centroids: { lng: number; lat: number }[],
  lng: number,
  lat: number,
): number {
  if (!centroids.length) return -1;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < centroids.length; i++) {
    const c = centroids[i]!;
    const dx = (c.lng - lng) * kx;
    const dy = (c.lat - lat) * ky;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

export function valuesAtCell(
  columns: Record<string, (number | null)[]>,
  cell: number,
): CoreValues {
  const out = {} as CoreValues;
  for (const p of CORE_PROPS) {
    const v = columns[p.stack]?.[cell];
    out[p.key] = v == null || !Number.isFinite(v) ? null : v;
  }
  return out;
}

export function emptyCoreValues(): CoreValues {
  const out = {} as CoreValues;
  for (const p of CORE_PROPS) out[p.key] = null;
  return out;
}

export function meanOf(values: (number | null | undefined)[]): number | null {
  let sum = 0;
  let n = 0;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    sum += v;
    n++;
  }
  return n ? sum / n : null;
}

/* * Mean of one high-res column over every stack cell whose center sits inside the zone. */
export function zoneMean(
  zone: GeniusZone,
  centroids: { lng: number; lat: number }[],
  column: (number | null)[] | undefined,
): number | null {
  if (!column?.length) return null;
  const polys = polygonsOf(zone.feature);
  const inside: number[] = [];
  for (let i = 0; i < centroids.length; i++) {
    const v = column[i];
    if (v == null || !Number.isFinite(v)) continue;
    if (pointInPolygons(centroids[i]!, polys)) inside.push(v);
  }
  return meanOf(inside);
}

/* * The sample fields the lab join and the Mehlich-3 seam read. A full `SoilSamplePoint` satisfies it. */
export type LabRow = Pick<
  SoilSamplePoint,
  | "zone_ref"
  | "sample_event_id"
  | "properties_8in"
  | "p_extractant"
  | "k_extractant"
> & {
  properties?: SoilSamplePoint["properties"];
  depth_in?: number | null;
  units?: SoilSamplePoint["units"];
};

/*
 *
 * The zone's lab reading for one nutrient, on the stored Mehlich-3 / 8″ basis the uncorrected model is on
 * (`sampleBlobToM3`, the FE twin of compute's `_sample_y`). The correction compares the two, so they must
 * share a basis — a Bray P or a lbs/ac row read raw would shift every core by the wrong amount.
 */
export function labValueForZone(
  rows: LabRow[],
  eventId: string,
  ordinal: number,
  prop: CorePropKey,
): number | null {
  const hits = rows.filter(
    (r) => r.sample_event_id === eventId && r.zone_ref === ordinal,
  );
  return meanOf(
    hits.map((r) => sampleBlobToM3(r as SoilSamplePoint)?.[prop] ?? null),
  );
}

/* * High-res pixel when it has a number; otherwise the stack cell. A null high-res read must not erase a real cell. */
export function preferReading(
  primary: number | null | undefined,
  fallback: number | null | undefined,
): number | null {
  if (primary != null && Number.isFinite(primary)) return primary;
  if (fallback != null && Number.isFinite(fallback)) return fallback;
  return null;
}

/* * Mean of `sample` at every point that falls inside the zone. */
export function meanInsideZone(
  zone: GeniusZone,
  points: { lng: number; lat: number }[],
  sample: (lng: number, lat: number) => number | null,
): number | null {
  const polys = polygonsOf(zone.feature);
  const vals: number[] = [];
  for (const p of points) {
    if (!pointInPolygons(p, polys)) continue;
    const v = sample(p.lng, p.lat);
    if (v != null && Number.isFinite(v)) vals.push(v);
  }
  return meanOf(vals);
}

/* * Lab minus the uncorrected core average. Added to every core in the zone so the cores' average matches the lab. */
export function coreCorrection(
  lab: number | null,
  modelMean: number | null,
): number | null {
  if (
    lab == null ||
    modelMean == null ||
    !Number.isFinite(lab) ||
    !Number.isFinite(modelMean)
  )
    return null;
  return lab - modelMean;
}

export interface CorrectedCore {
  lng: number;
  lat: number;
  /* * the zone's index in walk order */
  zoneIndex: number;
  /* * the zone's `zone_number` / ordinal — what the lab join and compute's provenance key on */
  zone: number;
  values: CoreValues;
}

/*
 *
 * Every core with its displayed values: the uncorrected cell plus that zone's core correction, per
 * nutrient, when the lab is known. The ONE source for the dots, the preview krige, and the saved map.
 */
export function correctedCores(
  placements: { lng: number; lat: number; values: CoreValues }[][],
  zones: GeniusZone[],
  labs: LabRow[],
  eventId: string | null,
): CorrectedCore[] {
  const out: CorrectedCore[] = [];
  placements.forEach((group, zi) => {
    const zone = zones[zi];
    const shift = {} as Record<CorePropKey, number | null>;
    for (const p of CORE_PROPS) {
      const modelMean = meanOf(group.map((c) => c.values[p.key]));
      const lab =
        zone && eventId
          ? labValueForZone(labs, eventId, zone.ordinal, p.key)
          : null;
      shift[p.key] = coreCorrection(lab, modelMean);
    }
    for (const core of group) {
      const values = emptyCoreValues();
      for (const p of CORE_PROPS) {
        const raw = core.values[p.key];
        if (raw == null || !Number.isFinite(raw)) continue;
        const corr = shift[p.key];
        values[p.key] = corr != null ? raw + corr : raw;
      }
      out.push({
        lng: core.lng,
        lat: core.lat,
        zoneIndex: zi,
        zone: zone?.ordinal ?? zi + 1,
        values,
      });
    }
  });
  return out;
}

/* * One nutrient's corrected cores, for the preview krige. */
export function correctedCorePoints(
  placements: { lng: number; lat: number; values: CoreValues }[][],
  zones: GeniusZone[],
  labs: LabRow[],
  eventId: string | null,
  nutrient: CorePropKey,
): { lng: number; lat: number; value: number }[] {
  const out: { lng: number; lat: number; value: number }[] = [];
  for (const c of correctedCores(placements, zones, labs, eventId)) {
    const v = c.values[nutrient];
    if (v != null) out.push({ lng: c.lng, lat: c.lat, value: v });
  }
  return out;
}

/* * Every zone of the walk has at least one core. */
export function everyZoneHasCores(
  placements: unknown[][],
  zones: GeniusZone[],
): boolean {
  return (
    zones.length > 0 &&
    zones.every((z) => (placements[z.index]?.length ?? 0) > 0)
  );
}

export function formatCoreValue(key: CorePropKey, v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const digits =
    (key === "p" || key === "k") && Math.abs(v) >= 100
      ? 0
      : CORE_PROPS.find((p) => p.key === key)!.digits;
  return v.toFixed(digits);
}

export function coreLabelLines(values: CoreValues): [string, string] {
  const text = (key: CorePropKey) =>
    `${CORE_PROPS.find((p) => p.key === key)!.label} ${formatCoreValue(key, values[key])}`;
  return [
    `${text("ph")}  ${text("p")}  ${text("k")}`,
    `${text("om")}  ${text("cec")}`,
  ];
}
